/**
 * useAnalyticsUIState Hook
 * 
 * Manages all UI state for the Analytics page:
 * - Loading states
 * - Date range filters
 * - Selected accounts/users
 * - Period selection
 * - Tab selection
 * - Aggregation mode
 * - Filter anchors (popovers)
 * - Animation states
 */

import { useState, useRef } from "react";
import type React from "react";

export interface UseAnalyticsUIStateParams {
  initialLoading?: boolean;
}

export interface UseAnalyticsUIStateReturn {
  // Loading state
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;

  // Date range
  fromDate: string;
  setFromDate: React.Dispatch<React.SetStateAction<string>>;
  toDate: string;
  setToDate: React.Dispatch<React.SetStateAction<string>>;

  // Account filters
  selectedAccounts: string[];
  setSelectedAccounts: React.Dispatch<React.SetStateAction<string[]>>;
  accountFilterAnchor: HTMLElement | null;
  setAccountFilterAnchor: React.Dispatch<React.SetStateAction<HTMLElement | null>>;

  // Period selection
  selectedPeriod: string;
  setSelectedPeriod: React.Dispatch<React.SetStateAction<string>>;
  currentPeriodOffset: number;
  setCurrentPeriodOffset: React.Dispatch<React.SetStateAction<number>>;

  // Tab and aggregation
  selectedTab: "expenses" | "incomes" | "adjustments";
  setSelectedTab: React.Dispatch<React.SetStateAction<"expenses" | "incomes" | "adjustments">>;
  aggregationMode: "category" | "group";
  setAggregationMode: React.Dispatch<React.SetStateAction<"category" | "group">>;

  // User filter
  userFilterAnchor: HTMLElement | null;
  setUserFilterAnchor: React.Dispatch<React.SetStateAction<HTMLElement | null>>;

  // Animation states
  shouldAnimate: boolean;
  setShouldAnimate: React.Dispatch<React.SetStateAction<boolean>>;
  readyToShowChart: boolean;
  setReadyToShowChart: React.Dispatch<React.SetStateAction<boolean>>;

  // Refs
  previousDataHashRef: React.MutableRefObject<string>;
  previousTabRef: React.MutableRefObject<"expenses" | "incomes" | "adjustments">;
  hasAnimatedOnMountRef: React.MutableRefObject<boolean>;
  isAnimatingTabChangeRef: React.MutableRefObject<boolean>;
}

/**
 * Hook to manage all UI state for Analytics page
 */
export function useAnalyticsUIState({ initialLoading = true }: UseAnalyticsUIStateParams = {}): UseAnalyticsUIStateReturn {
  // Loading state - can be initialized based on cached data
  const [loading, setLoading] = useState<boolean>(initialLoading);

  // Initialize date range with default current month values immediately
  // This prevents the visual change when dates are set in useEffect
  const getDefaultDates = () => {
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
    const toStr = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
    return { fromStr, toStr };
  };
  
  const defaultDates = getDefaultDates();
  const [fromDate, setFromDate] = useState<string>(defaultDates.fromStr);
  const [toDate, setToDate] = useState<string>(defaultDates.toStr);

  // Account filters
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [accountFilterAnchor, setAccountFilterAnchor] = useState<HTMLElement | null>(null);

  // Period selection
  const [selectedPeriod, setSelectedPeriod] = useState<string>("Monthly");
  const [currentPeriodOffset, setCurrentPeriodOffset] = useState<number>(0);

  // Tab and aggregation
  const [selectedTab, setSelectedTab] = useState<"expenses" | "incomes" | "adjustments">("expenses");
  const [aggregationMode, setAggregationMode] = useState<"category" | "group">("group");

  // User filter
  const [userFilterAnchor, setUserFilterAnchor] = useState<HTMLElement | null>(null);

  // Animation states
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
  const [readyToShowChart, setReadyToShowChart] = useState<boolean>(false);

  // Refs
  const previousDataHashRef = useRef<string>("");
  const previousTabRef = useRef<"expenses" | "incomes" | "adjustments">("expenses");
  const hasAnimatedOnMountRef = useRef<boolean>(false);
  const isAnimatingTabChangeRef = useRef<boolean>(false);

  return {
    // Loading state
    loading,
    setLoading,

    // Date range
    fromDate,
    setFromDate,
    toDate,
    setToDate,

    // Account filters
    selectedAccounts,
    setSelectedAccounts,
    accountFilterAnchor,
    setAccountFilterAnchor,

    // Period selection
    selectedPeriod,
    setSelectedPeriod,
    currentPeriodOffset,
    setCurrentPeriodOffset,

    // Tab and aggregation
    selectedTab,
    setSelectedTab,
    aggregationMode,
    setAggregationMode,

    // User filter
    userFilterAnchor,
    setUserFilterAnchor,

    // Animation states
    shouldAnimate,
    setShouldAnimate,
    readyToShowChart,
    setReadyToShowChart,

    // Refs
    previousDataHashRef,
    previousTabRef,
    hasAnimatedOnMountRef,
    isAnimatingTabChangeRef,
  };
}
